import React from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import { buildEmailAction, hoursSummary } from '../../utils/helpers';
import ClosuresSettings from './ClosuresSettings';

// Shop — extracted from Settings.jsx on 17 Sep 2026. The markup is
// unchanged; every value it needs comes from the Settings context rather than
// from props, so this move could not alter behaviour.
export default function SettingsShop() {
  const { T, setSaved, setSettings, setShopHoursDay, settings } = useSettings();
  return (
    <>
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Labor Rate</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Used to auto-calculate the price when adding a custom labor line from a ticket (Hours + Minutes × rate). The tech can always type over the calculated price by hand.
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Hourly labor rate</span>
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <span style={{ color:T.textSub, fontSize:14 }}>$</span>
            <input style={{ ...S.thresholdInput, width:80, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }}
              type="number" min="0" step="1" value={settings.laborRate ?? ''}
              placeholder="e.g. 95"
              onChange={e => { setSettings({ ...settings, laborRate: e.target.value ? parseFloat(e.target.value) : null }); setSaved(false); }} />
          </div>
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>per hour — leave blank to enter labor prices manually</span>
        </div>
      </div>

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Shop Profile</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>This information appears in pickup reminder emails sent to customers. Fill in your shop details to make emails look professional.</div>

        {[
          { key:'name', label:'Shop Name', placeholder:'Your Shop Name' },
          { key:'address', label:'Address', placeholder:'123 Main St, Your Town, NJ 07000' },
          { key:'phone', label:'Phone', placeholder:'(973) 555-1234' },
          { key:'email', label:'Store Email', placeholder:'service@yourshop.com' },
          { key:'website', label:'Website', placeholder:'www.yourshop.com' },
          // Left empty, it is written from the day-by-day hours below (3 Nov).
          { key:'hours', label:'Store Hours', placeholder: hoursSummary((settings.shopProfile || {}).hoursStructured) || 'Mon-Fri 10-6 · Sat 10-5 · Sun Closed' },
          { key:'signature', label:'Email Signature', placeholder:'Your Service Team' },
          { key:'logoUrl', label:'Logo URL', placeholder:'https://yoursite.com/logo.png (paste a direct image link)' },
        ].map(field => (
          <div key={field.key} style={S.thresholdRow}>
            <span style={{ ...S.thresholdLabel, minWidth:160, color:T.text }}>{field.label}</span>
            <input
              type="text"
              value={(settings.shopProfile || {})[field.key] || ''}
              placeholder={field.placeholder}
              onChange={e => {
                setSettings({ ...settings, shopProfile: { ...(settings.shopProfile || {}), [field.key]: e.target.value } });
                setSaved(false);
              }}
              style={{ flex:1, padding:'7px 12px', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, color:T.text, fontSize:13, fontFamily:"'Inter', sans-serif", outline:'none' }}
            />
          </div>
        ))}

        <div style={{ marginTop:16, marginBottom:6, fontSize:12, color:T.text, fontWeight:600 }}>Store hours, day by day</div>
        <div style={{ fontSize:11, color:T.textSub, marginBottom:10, fontStyle:'italic', paddingLeft:2, lineHeight:1.6 }}>
          ⓘ These are your hours everywhere: the after-hours auto-reply, your website (its hours and "Open now"), and, when the <strong>Store Hours</strong> field above is empty, the hours in customer emails and texts (shown greyed in that field). Write your own wording there only if you want it different. Holidays and other changes go in <strong>Closures and special hours</strong> below.
        </div>
        {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((label, i) => {
          const d = ((settings.shopProfile || {}).hoursStructured || [])[i] || { closed: true, open: '10:00', close: '18:00' };
          return (
            <div key={label} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
              <span style={{ width:96, fontSize:13, color:T.text }}>{label}</span>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.textSub, cursor:'pointer', width:86 }}>
                <input type="checkbox" checked={!!d.closed}
                  onChange={e => setShopHoursDay(i, { closed: e.target.checked })}
                  style={{ cursor:'pointer' }} />
                Closed
              </label>
              {!d.closed && (
                <>
                  <input type="time" value={d.open || '10:00'}
                    onChange={e => setShopHoursDay(i, { open: e.target.value })}
                    style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'5px 8px', color:T.text, fontSize:12.5, outline:'none', fontFamily:'inherit' }} />
                  <span style={{ fontSize:12, color:T.textSub }}>to</span>
                  <input type="time" value={d.close || '18:00'}
                    onChange={e => setShopHoursDay(i, { close: e.target.value })}
                    style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'5px 8px', color:T.text, fontSize:12.5, outline:'none', fontFamily:'inherit' }} />
                </>
              )}
            </div>
          );
        })}

        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:160, color:T.text }}>Abandoned bike policy</span>
          <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
            <span style={{ ...S.thresholdUnit, color:T.textSub }}>Bikes unclaimed after</span>
            <input
              type="number" min="1" max="365"
              value={(settings.shopProfile || {}).abandonedDays || 30}
              onChange={e => {
                setSettings({ ...settings, shopProfile: { ...(settings.shopProfile || {}), abandonedDays: parseInt(e.target.value)||30 } });
                setSaved(false);
              }}
              style={{ width:60, padding:'6px 10px', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, color:T.text, fontSize:14, fontFamily:"'IBM Plex Mono', monospace", textAlign:'center' }}
            />
            <span style={{ ...S.thresholdUnit, color:T.textSub }}>days are considered abandoned</span>
          </div>
        </div>

        {/* Preview */}
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:11, color:'#555', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Email Preview</div>
          <div style={{ background:T.noteBox, borderRadius:8, padding:'16px', fontFamily:"'IBM Plex Mono', monospace", fontSize:11, color:T.textSub, lineHeight:1.8, whiteSpace:'pre-wrap', border:`1px solid ${T.border}` }}>
            {buildEmailAction('customer@email.com', 'John Customer', 'Trek Marlin', settings.shopProfile || {}).plain}
          </div>
        </div>
      </div>

      <ClosuresSettings />
    </>
  );
}
